'use client'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useDictionary } from '@/contexts/DictionaryContext'
import { motion } from 'framer-motion'
import { ArrowRight, Calendar } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import type { BlogPost } from '../app/[lang]/blog/lib/posts'

interface BlogSummarySectionProps {
  blogPosts: BlogPost[]
  lang: string
}

export function BlogSummarySection({ blogPosts, lang }: BlogSummarySectionProps) {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)
  const { locale } = useDictionary()

  const dictionary =
    locale === 'tr'
      ? {
          title: 'Blog Yazılarımız',
          readTime: 'Okuma Süresi',
          minutes: 'dakika',
          readMore: 'Devamını Oku',
          publishedOn: 'Yayınlanma',
          category: 'Kategori',
        }
      : {
          title: 'Our Blog Posts',
          readTime: 'Read Time',
          minutes: 'minutes',
          readMore: 'Read More',
          publishedOn: 'Published on',
          category: 'Category',
        }

  if (!blogPosts || blogPosts.length === 0) {
    return null
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  }

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        damping: 12,
        stiffness: 100,
      },
    },
  }

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  }

  const dividerVariants = {
    hidden: { width: 0 },
    visible: {
      width: '8rem',
      transition: {
        duration: 0.8,
        ease: 'easeOut' as const,
        delay: 0.3,
      },
    },
  }

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat(locale === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date)
  }

  return (
    <section className='py-20 relative overflow-hidden'>
      {/* Decorative elements */}
      <div className='absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none'>
        <div className='absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-blue-500/5 blur-3xl'></div>
        <div className='absolute top-[60%] -right-[5%] w-[30%] h-[40%] rounded-full bg-blue-500/5 blur-3xl'></div>
      </div>

      <div className='container mx-auto px-4 relative z-10'>
        <motion.div
          className='mb-16 text-center'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, margin: '-100px' }}
        >
          <motion.h2
            className='text-5xl font-bold mb-4 text-white tracking-tight bg-clip-text bg-gradient-to-r from-white to-blue-200'
            variants={titleVariants}
          >
            {dictionary.title}
          </motion.h2>
          <motion.div
            className='h-1.5 bg-gradient-to-r from-blue-600 to-cyan-400 mx-auto rounded-full'
            variants={dividerVariants}
          ></motion.div>
        </motion.div>

        <motion.div
          className='grid grid-cols-1 md:grid-cols-2 gap-8'
          variants={containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, margin: '-50px' }}
        >
          {blogPosts.map((post, index) => {
            const postSlug = locale === 'tr' ? post.slug_tr : post.slug_en
            const postTitle = locale === 'tr' ? post.title_tr : post.title_en
            const postExcerpt = locale === 'tr' ? post.excerpt_tr : post.excerpt_en

            return (
              <motion.div
                key={postSlug}
                variants={cardVariants}
                className={index === 0 ? 'md:col-span-2' : ''}
              >
                <Link
                  href={`/${lang}/blog/post/${postSlug}`}
                  className='block h-full'
                >
                  <motion.div
                    whileHover={{
                      transition: { duration: 0.2 },
                    }}
                    className='h-full'
                  >
                    <Card
                      className={`border border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/5 cursor-pointer group overflow-hidden h-full ${
                        hoveredCard === postSlug ? 'ring-2 ring-blue-500/30' : ''
                      }`}
                      onMouseEnter={() => setHoveredCard(postSlug)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      <div className={`flex flex-col h-full ${index === 0 ? 'md:flex-row' : ''}`}>
                        <div className={`relative overflow-hidden ${index === 0 ? 'md:w-2/5' : 'h-48'}`}>
                          <Image
                            src={post.coverImage || `/placeholder.svg?height=400&width=600`}
                            alt={postTitle}
                            width={600}
                            height={400}
                            className='object-cover w-full h-full transition-transform duration-700 group-hover:scale-110'
                          />
                          <div className='absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent'></div>
                          <Badge
                            className='absolute top-4 left-4 bg-blue-500/90 hover:bg-blue-600 text-white border-none'
                            variant='secondary'
                          >
                            {post.category}
                          </Badge>
                        </div>

                        <div className={`flex flex-col ${index === 0 ? 'md:w-3/5' : ''}`}>
                          <CardHeader>
                            <CardTitle className='text-2xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300 line-clamp-2'>
                              {postTitle}
                            </CardTitle>
                          </CardHeader>

                          <CardContent className='flex-grow'>
                            <CardDescription className='text-slate-300 line-clamp-3 text-base'>{postExcerpt}</CardDescription>
                          </CardContent>

                          <CardFooter className='flex flex-col sm:flex-row sm:justify-between gap-4 pt-2 border-t border-slate-700/50'>
                            <div className='flex items-center gap-4 text-xs text-slate-400'>
                              <div className='flex items-center gap-1.5'>
                                <Calendar className='w-3.5 h-3.5' />
                                <span>{formatDate(post.date)}</span>
                              </div>
                            </div>

                            <motion.div
                              className='flex items-center gap-1 text-blue-400 font-medium text-sm'
                              animate={hoveredCard === postSlug ? { x: 5 } : { x: 0 }}
                            >
                              {dictionary.readMore}
                              <ArrowRight className='w-4 h-4 transition-transform group-hover:translate-x-1' />
                            </motion.div>
                          </CardFooter>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                </Link>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}